import {
  BatchIcon,
  ClockIcon,
  DeviceIcon,
  ExpiryIcon,
  HistoryIcon,
  HomeIcon,
  ImportIcon,
  OfflineDataIcon,
  OrdersIcon,
  PreferencesIcon,
  RegisterIcon,
  RxIcon,
  SaleIcon,
  SettingsIcon,
  ShortcutsIcon,
  StockIcon,
  StoreIcon,
  SwapIcon,
  TroubleshootIcon,
} from '@/components/icons';
import type { ComponentType } from 'react';

import type { Permission } from '@/auth/permissions';

export type NavItem = {
  key: string;
  path: string;
  label: string;
  icon?: ComponentType;
  /** Hidden unless the signed-in user holds at least one of these. */
  permissions?: Permission[];
  /**
   * Hidden unless the BRANCH has been given this by an admin.
   *
   * Separate from `permissions` because they answer different questions: a
   * permission is about the person at the counter, a feature is about what the
   * shop was signed up for. A pharmacist with every permission in the matrix
   * still has no order queue at a counter-only shop.
   */
  feature?: 'marketplace' | 'prescriptions';
  /**
   * Backend answers 501 today; shown greyed so the shape is discoverable.
   *
   * Nothing here uses it yet. It stays declared so `SidebarNav` is the same
   * file in both apps - the sidebar is meant to be one component the two
   * products share, and a field dropped on one side is how that stops being
   * true.
   */
  stubbed?: boolean;
  /**
   * Carries the count of orders waiting to be accepted.
   *
   * Declared on the ITEM rather than matched on `key === 'orders'` in the
   * sidebar, so the two files do not have to agree about a string. Only one
   * row has it, and it is the only number in this app that changes without
   * anybody here doing anything.
   */
  badge?: BadgeKind;
};

/** The live counts a nav row can carry: work that arrived on its own. */
export type BadgeKind = 'new-orders' | 'new-prescriptions';

/**
 * A section that folds. Its own children are hidden until it is opened, which
 * is the whole point - see the note on NAVIGATION below.
 */
export type NavGroup = {
  key: string;
  label: string;
  icon: ComponentType;
  items: NavItem[];
};

export type NavEntry = NavItem | NavGroup;

export function isGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry;
}

/**
 * The sidebar: a short flat head, then subject groups.
 *
 * Same shape as the admin panel, which is the point - the two sit on the same
 * desk and a person moving between them should not have to learn two ideas of
 * where things live. Its rules, and now ours:
 *
 *   **Only what you reach for with a customer in front of you stays flat.**
 *   Thirteen links at one level is not navigation, it is an index: you read all
 *   of it every time because nothing says which part matters now.
 *
 *   **A group is one subject, and never longer than five.** Past that the list
 *   stops being scannable and the group stops meaning anything - which is
 *   exactly what had happened to Stock. It held seven rows covering two
 *   different jobs: buying goods in, which is done when a delivery arrives and
 *   involves a supplier and an invoice, and minding the shelf, which is done on
 *   a slow afternoon and involves no money at all. They are now **Purchasing**
 *   and **Stock**, three and four rows, and either one can be read at a glance.
 *
 *   **The group icon repeats on its primary child.** Purchasing → Purchase,
 *   the way admin does Catalog → Medicines: it marks which row the section
 *   exists for, and is what a collapsed rail falls back to.
 *
 * Anything platform-side - commission, other branches, users, the audit trail -
 * belongs in the admin panel and is deliberately absent here.
 */
export const NAVIGATION: NavEntry[] = [
  { key: 'today', path: '/', label: 'Today', icon: HomeIcon },
  {
    key: 'orders',
    path: '/orders',
    label: 'Orders',
    icon: OrdersIcon,
    permissions: ['order:read:any'],
    feature: 'marketplace',
    badge: 'new-orders',
  },
  {
    key: 'prescriptions',
    path: '/prescriptions',
    label: 'Prescriptions',
    icon: RxIcon,
    permissions: ['prescription:review'],
    feature: 'prescriptions',
    badge: 'new-prescriptions',
  },
  {
    key: 'sale',
    path: '/sale',
    label: 'Sale',
    icon: SaleIcon,
    permissions: ['order:fulfil'],
  },
  /*
   * Replaced the Bills row, and the name change is the point.
   *
   * Bills listed what the TILL had issued, which is half of what a shop sold -
   * the other half arrived through the app and was readable only on the order
   * queue, grouped by what still needed packing rather than by date. Nobody
   * could answer "what did we take last Tuesday" from either. The register is
   * both halves in one dated list, and everything Bills did lives on inside it.
   */
  {
    key: 'register',
    path: '/register',
    label: 'Register',
    icon: RegisterIcon,
    permissions: ['order:read:any'],
  },

  /*
   * Goods coming IN, and the money owed for them. Worked in a block when a
   * delivery arrives, which is why it folds - and why it is not the same
   * section as the shelf below it.
   */
  {
    key: 'purchasing',
    label: 'Purchasing',
    icon: ImportIcon,
    items: [
      {
        key: 'purchase',
        path: '/purchase',
        label: 'Purchase',
        icon: ImportIcon,
        permissions: ['purchase:write'],
      },
      {
        key: 'purchases',
        path: '/purchases',
        label: 'Purchase history',
        icon: HistoryIcon,
        permissions: ['purchase:read'],
      },
      {
        key: 'suppliers',
        path: '/suppliers',
        label: 'Suppliers',
        icon: StoreIcon,
        permissions: ['purchase:read'],
      },
    ],
  },

  /*
   * What is on the shelf right now, and how it got that way. No money in this
   * section at all - that is the line between it and Purchasing.
   */
  {
    key: 'stock',
    label: 'Stock',
    icon: StockIcon,
    items: [
      {
        key: 'onhand',
        path: '/stock',
        label: 'On hand',
        icon: StockIcon,
        permissions: ['inventory:read'],
      },
      {
        key: 'batches',
        path: '/batches',
        label: 'Batches',
        icon: BatchIcon,
        permissions: ['inventory:read'],
      },
      {
        key: 'alerts',
        path: '/alerts',
        label: 'Expiry & low stock',
        icon: ExpiryIcon,
        permissions: ['inventory:read'],
      },
      {
        key: 'ledger',
        path: '/ledger',
        label: 'Stock ledger',
        icon: SwapIcon,
        permissions: ['inventory:read'],
      },
    ],
  },

  /*
   * Flat, and staying flat.
   *
   * A group of one is a folder with a single file in it. This is also not a
   * setting despite looking like one: the shutter going up late, or a closure
   * for a festival, is an operational decision a shop makes during the day and
   * the availability pill in the header links straight here. Admin does the
   * same thing in reverse, filing `/settings/fees` under Delivery rather than
   * under Settings, because a row belongs with its subject.
   *
   * No listings entry either. What the platform sells off this shelf, and at
   * what price, is decided in the admin panel - the shop supplies the stock and
   * fulfils the orders. Its lever here is opening hours and the OPEN/BUSY flag,
   * both of which are genuinely only the shop's to know.
   */
  {
    key: 'hours',
    path: '/hours',
    label: 'Opening hours',
    icon: ClockIcon,
    permissions: ['pharmacy:read'],
  },
];

/**
 * The settings pages, in the order the settings tabs show them.
 *
 * Not in the sidebar any more. Unfolded there they were six rows at its foot,
 * and on a laptop they pushed Stock off the screen to make room for pages a
 * shop opens once a month. The sidebar has one Settings row (below), and
 * SettingsLayout draws these as tabs above whichever is open.
 */
export const SETTINGS_PAGES: NavItem[] = [
  { key: 'device', path: '/settings', label: 'This device', icon: DeviceIcon },
  { key: 'offline', path: '/settings/offline', label: 'Offline data', icon: OfflineDataIcon },
  { key: 'counter-prefs', path: '/settings/counter', label: 'Counter', icon: PreferencesIcon },
  { key: 'keyboard', path: '/settings/keyboard', label: 'Keyboard', icon: ShortcutsIcon },
  { key: 'shop', path: '/settings/shop', label: 'This shop', icon: StoreIcon },
  {
    key: 'troubleshooting',
    path: '/settings/troubleshooting',
    label: 'Troubleshooting',
    icon: TroubleshootIcon,
  },
];

/**
 * The settings pages as a group, for the topbar's crumb: "Settings / Offline
 * data". It is not rendered as a sidebar section.
 */
export const SETTINGS_SECTION: NavGroup = {
  key: 'settings',
  label: 'Settings',
  icon: SettingsIcon,
  items: SETTINGS_PAGES,
};

export const NAVIGATION_FOOTER: NavEntry[] = [
  /*
   * One row. It is the current row on every settings page, because the
   * sidebar matches by prefix: /settings/offline is "under" /settings.
   */
  { key: 'settings', path: '/settings', label: 'Settings', icon: SettingsIcon },
];

/** Every destination, flattened - used to resolve the active route. */
export const ALL_NAV_ITEMS: NavItem[] = [
  ...NAVIGATION.flatMap((entry) => (isGroup(entry) ? entry.items : [entry])),
  /*
   * Before the footer's Settings row, so that on /settings itself the crumb
   * resolves to "This device" (inside the Settings group) rather than to the
   * bare Settings row - both share the path, and the first match wins a tie.
   */
  ...SETTINGS_PAGES,
  ...NAVIGATION_FOOTER.flatMap((entry) => (isGroup(entry) ? entry.items : [entry])),
];

/**
 * The section a path lives in, so it can be opened when you land inside it.
 *
 * Both lists, not just NAVIGATION: Settings is a folding section now, and
 * searching only the main list would land you on Troubleshooting with the
 * section still shut and nothing in the sidebar marking where you are.
 */
export function groupKeyForPath(path: string): string | undefined {
  return [...NAVIGATION, ...NAVIGATION_FOOTER].find(
    (entry) => isGroup(entry) && entry.items.some((item) => item.path === path),
  )?.key;
}
